const SELECTORS = {
    form: '[data-js="product-registration-form"]',
    message: '[data-content="product-registration-message"]',
    submitButton: '[type="submit"]',
    requiredField: 'input[required], select[required]',
    checkboxGroup: 'fieldset[data-minimum-selection]',
};

const ATTR = {
    endpoint: 'data-form-endpoint',
    fieldName: 'data-field-name',
    formId: 'data-form-id',
    minimumSelection: 'data-minimum-selection',
    state: 'data-state',
    successMessage: 'data-success-message',
};

const EVENT_HANDLERS = {
    submit: new Map([[SELECTORS.form, { handler: handleProductRegistrationSubmit }]]),
};

let isInitialized = false;

function initProductRegistrationForm() {
    if (isInitialized) return;

    isInitialized = true;

    Object.entries(EVENT_HANDLERS).forEach(([eventName, handlers]) => {
        document.addEventListener(eventName, (event) => {
            for (const [selector, config] of handlers) {
                const target = event.target.closest(selector);

                if (!target) continue;

                config.handler(event, target);
                break;
            }
        });
    });
}

async function handleProductRegistrationSubmit(event, form) {
    event.preventDefault();

    const endpoint = form.getAttribute(ATTR.endpoint);

    if (!endpoint) {
        updateFormMessage(form, 'error', 'This form is missing a submission endpoint.');
        return;
    }

    const errors = [
        ...validateRequiredFields(form),
        ...validateCheckboxGroups(form),
    ];

    if (errors.length > 0) {
        updateFormMessage(form, 'error', errors.join(' '));
        return;
    }

    updateSubmitState(form, true);
    updateFormMessage(form, 'loading', 'Submitting registration...');

    try {
        const formData = new FormData(form);
        formData.append('form-id', form.getAttribute(ATTR.formId) || form.id);

        const response = await fetch(endpoint, {
            method: 'POST',
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`Registration request failed with HTTP ${response.status}`);
        }

        handleProductRegistrationSuccess(form);
    } catch (error) {
        console.error('There was an issue submitting the product registration form:', error);
        updateSubmitState(form, false);
        updateFormMessage(
            form,
            'error',
            'There was an issue submitting your registration. Please try again.'
        );
    }
}

function validateRequiredFields(form) {
    return Array.from(form.querySelectorAll(SELECTORS.requiredField)).reduce((errors, field) => {
        if (field.value.trim()) return errors;

        errors.push(`Please fill out the ${parseFieldName(field)} field.`);

        return errors;
    }, []);
}

function validateCheckboxGroups(form) {
    return Array.from(form.querySelectorAll(SELECTORS.checkboxGroup)).reduce((errors, group) => {
        const minimumSelection = parseInt(group.getAttribute(ATTR.minimumSelection), 10);
        const checkedCount = group.querySelectorAll('input[type="checkbox"]:checked').length;

        if (!Number.isFinite(minimumSelection) || checkedCount >= minimumSelection) {
            return errors;
        }

        const productLabel = minimumSelection === 1 ? 'product' : 'products';
        errors.push(`Please select at least ${minimumSelection} ${productLabel}.`);

        return errors;
    }, []);
}

function parseFieldName(field) {
    const explicitName = field.getAttribute(ATTR.fieldName);

    if (explicitName) return explicitName;

    const label = field.id ? document.querySelector(`label[for="${field.id}"]`) : null;

    return label?.textContent.trim().replace('*', '') || 'required';
}

function updateSubmitState(form, isDisabled) {
    const submitButton = form.querySelector(SELECTORS.submitButton);

    if (!submitButton) return;

    submitButton.disabled = isDisabled;
    submitButton.setAttribute('aria-busy', isDisabled ? 'true' : 'false');
}

function updateFormMessage(form, state, message) {
    const messageElement = form.querySelector(SELECTORS.message);

    if (!messageElement) return;

    messageElement.hidden = false;
    messageElement.textContent = message;
    messageElement.setAttribute(ATTR.state, state);

    if (state === 'error') {
        messageElement.setAttribute('role', 'alert');
        messageElement.focus();
    } else {
        messageElement.setAttribute('role', 'status');
    }
}

function handleProductRegistrationSuccess(form) {
    const successMessage =
        form.getAttribute(ATTR.successMessage) ||
        'Success! Your product registration has been submitted.';

    updateFormMessage(
        form,
        'success',
        successMessage
    );

    form.reset();
    updateSubmitState(form, false);
    form.setAttribute(ATTR.state, 'success');
}

initProductRegistrationForm();
